/*
 * Copyright (C) 2026 to Present Applause App Quality, Inc. All rights reserved.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
  QueryList,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { AvatarModule } from '@ng-falcon/features/avatar/avatar.module';
import { PillModule } from '@ng-falcon/pill/pill.module';
import { Subscription } from 'rxjs';
import { AvatarImageComponent } from '@ng-falcon/features/avatar/avatar-image/avatar-image.component';
import {
  TESTER_NAVIGATION_HEADER_SERVICE,
  NavHeaderUser,
  NavHeaderAccountUrls,
  TesterNavigationHeaderService,
} from '../../tester-navigation-header/tester-navigation-header.service';
import { TesterNavigationMobileMenuService } from '../../tester-navigation-mobile-menu.service';

const COPY_FEEDBACK_DURATION_MS = 2000;

@Component({
  selector: 'ngf-tester-navigation-sidebar-account',
  standalone: true,
  imports: [MatIconModule, AvatarModule, PillModule],
  templateUrl: './tester-navigation-sidebar-account.component.html',
  styleUrls: ['./tester-navigation-sidebar-account.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class TesterNavigationSidebarAccountComponent implements OnInit, OnDestroy {
  @ViewChildren(AvatarImageComponent) avatarImages?: QueryList<AvatarImageComponent>;

  currentUser: NavHeaderUser | null = null;
  accountUrls: NavHeaderAccountUrls | null = null;
  isExpanded = false;
  isTextCopied = false;

  private copyTimeoutId: number | null = null;
  private menuSub = Subscription.EMPTY;

  constructor(
    @Optional() @Inject(TESTER_NAVIGATION_HEADER_SERVICE)
    readonly headerService: TesterNavigationHeaderService | null,
    private mobileMenuService: TesterNavigationMobileMenuService,
    private cdr: ChangeDetectorRef,
  ) {}

  get isTTL(): boolean {
    return this.currentUser?.isTTL === true;
  }

  get isLite(): boolean {
    return this.currentUser?.isLite === true;
  }

  ngOnInit(): void {
    if (this.headerService) {
      this.headerService.getCurrentUser().then(user => {
        this.currentUser = user;
        this.cdr.markForCheck();
      });
      this.headerService.getAccountUrls().then(urls => {
        this.accountUrls = urls;
        this.cdr.markForCheck();
      });
    }
    this.menuSub = this.mobileMenuService.isOpen$.subscribe(open => {
      if (!open && this.isExpanded) {
        this.isExpanded = false;
        this.cdr.markForCheck();
      }
    });
  }

  ngOnDestroy(): void {
    this.clearCopyTimeout();
    this.menuSub.unsubscribe();
  }

  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
    this.cdr.markForCheck();
  }

  onMenuLinkClick(): void {
    this.mobileMenuService.close();
  }

  onCopyUserId(): void {
    const userId = this.currentUser?.platformId;
    if (!userId) return;
    this.clearCopyTimeout();
    navigator.clipboard.writeText(String(userId)).then(() => {
      this.isTextCopied = true;
      this.cdr.markForCheck();
      this.copyTimeoutId = window.setTimeout(() => {
        this.isTextCopied = false;
        this.copyTimeoutId = null;
        this.cdr.detectChanges();
      }, COPY_FEEDBACK_DURATION_MS);
    }).catch(() => {});
  }

  onSignOut(): void {
    this.headerService?.logout();
  }

  private clearCopyTimeout(): void {
    if (this.copyTimeoutId) {
      clearTimeout(this.copyTimeoutId);
      this.copyTimeoutId = null;
    }
  }
}
